import { getMcoModal } from '@services/cms/mco-modal';
import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { CACHE_TAGS } from '@services/http/http';
import { logger } from '@services/loggers';

import { ApiOptionType, ProductsWithChildrenApiReturnType, SapiPlanType } from '@app-types/api';
import { OPTION_GENCODES, OptionType, PlanType } from '@app-types/product';

import { OFFER_BLOCKED } from '@constants/app';

const appConf = getAppConfig();

export const getBlockedOption = async (): Promise<OptionType | undefined> => {
    const optionsList = await getPlansAndOptions(String(OFFER_BLOCKED));
    return optionsList ? optionsList.options[0] : undefined;
};

export const getPlansAndOptions = async (
    optionGencodeList: string | string[],
    planGencodeList?: string,
): Promise<{
    options: OptionType[];
    plans: SapiPlanType[];
}> => {
    const listOptionGencodes = Array.isArray(optionGencodeList) ? optionGencodeList.join(',') : optionGencodeList;
    const gencodes = planGencodeList ? `${listOptionGencodes},${planGencodeList}` : listOptionGencodes;
    const path = `${appConf.bytelApi.url}/ventes/produits?withChildren=false&listGencode=${gencodes}`;

    let result: ProductsWithChildrenApiReturnType['produits'] = {};

    try {
        const response = await apiHttpService.get<ProductsWithChildrenApiReturnType>(path, undefined, {
            ttl: parseInt(getAppConfig().cache.ttl.products),
            tags: [CACHE_TAGS.PRODUCTS],
        });
        result = response.produits;
    } catch (error) {
        logger.error('path: ', path);
        logger.error('[error productsWithChildren]: ', error);
    }

    const options: OptionType[] = Object.values(result)
        .filter((item): item is ApiOptionType => 'gencode' in item && optionGencodeList.includes(item.gencode))
        .map((option) => ({
            gencode: option.gencode,
            price: option.price,
            image: option.small_image,
            name: option.name,
            pennantText: option.pennant_text,
            promoLabel: option.wording_promo,
        }));

    const plans: SapiPlanType[] = planGencodeList
        ? Object.values(result).filter(
              (item): item is SapiPlanType => 'gencode' in item && planGencodeList.includes(item.gencode),
          )
        : [];

    return {
        options,
        plans,
    };
};

export async function fetchOptionSouscriptiblesApi(gencode: string): Promise<string[]> {
    type ResponseType = {
        offresSecondaires: { idOption: string }[];
    };

    const path = getAppConfig().bytelApi.url + '/referentiel/offres/options-souscriptibles';
    return apiHttpService
        .post<ResponseType>(
            path,
            {
                parcours: 'ACTIVATION',
                univers: 'MOBILE',
                cibleClient: 'GP',
                offresPanier: { idAbonnement: gencode },
            },
            {
                ttl: parseInt(getAppConfig().cache.ttl.products),
                tags: [CACHE_TAGS.PRODUCTS],
            },
        )
        .then((res) => {
            let listOptionsSouscriptibles: string[] = [];
            if (res.offresSecondaires && res.offresSecondaires.length > 0) {
                listOptionsSouscriptibles = Array.from(res.offresSecondaires, (opt) => opt.idOption);
            }
            return listOptionsSouscriptibles;
        })
        .catch((err) => {
            logger.error('Error while fetching "options souscriptibles" :', err);
            return [];
        });
}

export async function fetchProductsOptions(planList: PlanType[]): Promise<PlanType[]> {
    const commonOption: string[] = [
        OPTION_GENCODES.PARENTAL_CONTROL_BASIC,
        OPTION_GENCODES.PARENTAL_CONTROL_PREMIUM,
        OPTION_GENCODES.FIVE_G,
        OPTION_GENCODES.BYOU_MAX,
    ];

    const mcoModal = await getMcoModal();
    mcoModal?.map((O) => commonOption.push(O.gencode));

    try {
        const optionsAndPlansRes = await getPlansAndOptions(
            commonOption.join(','),
            planList.map((plan) => plan.gencode).join(','),
        );

        const option5g = optionsAndPlansRes.options.find((option) => option.gencode === OPTION_GENCODES.FIVE_G) || {};
        const optionByouMax =
            optionsAndPlansRes.options.find((option) => option.gencode === OPTION_GENCODES.BYOU_MAX) || {};

        // add content editor data
        const optionsList = optionsAndPlansRes.options.map((option) => {
            const op = mcoModal?.find((mm) => mm.gencode === option.gencode);
            return {
                ...option,
                backgroundImage: {
                    default: op?.background_image.public_url,
                    portrait: op?.background_image_portrait.public_url,
                },
            };
        });

        return await Promise.all(
            planList.map(async (p) => {
                const optionSouscriptibles: string[] = await fetchOptionSouscriptiblesApi(p.gencode);

                const compatibleUpSellsProducts = optionsList?.filter((option) =>
                    optionSouscriptibles?.includes(option.gencode),
                );
                const hasOption5g = compatibleUpSellsProducts?.find((u) => u.gencode === OPTION_GENCODES.FIVE_G);
                const hasOptionByouMax = compatibleUpSellsProducts?.find((u) => u.gencode === OPTION_GENCODES.BYOU_MAX);
                const foundPlan = optionsAndPlansRes.plans.find((plan) => plan.gencode === p.gencode);
                return {
                    ...p,
                    dataSize: foundPlan?.data_size,
                    maxConnections: foundPlan?.max_connection,
                    locked: foundPlan?.locked,
                    smsMms: foundPlan?.sms_mms,
                    roamingEnvelope: foundPlan?.roaming_envelope,
                    fromAbroad: foundPlan?.from_abroad,
                    options: compatibleUpSellsProducts,
                    option5g: hasOption5g ? option5g : {},
                    optionByouMax: hasOptionByouMax ? optionByouMax : {},
                    sims: {},
                } as PlanType;
            }),
        );
    } catch (error) {
        throw String(error);
    }
}
